import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EstabelecimentoDataService,
  EstabelecimentoService,
  NichoTermosService,
  ProfissionalDataService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { I18nService } from '@nomoh-monorepo/data-access-shared-i18n';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { fechamentoMaiorQueAbertura } from '@nomoh-monorepo/shared-angular-util-tools';
import {
  BottomSheetComponent,
  DeviceService,
  FormFieldComponent,
  NMultiselectComponent,
  PhoneInputComponent,
} from '@nomoh-monorepo/shared-common-ui';
import {
  ehPaisBrasil,
  EstadosConst,
  IEndereco,
  IEstabelecimento,
  IImagem,
  IPaisOption,
  listarPaises,
  NichoEstabelecimentoEnum,
  normalizarCodigoPais,
  PAIS_BRASIL
} from '@nomoh-monorepo/shared-types';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';

@Component({
  selector: 'lib-editar-estabelecimento-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    InputMaskModule,
    DatePickerModule,
    MultiSelectModule,
    SelectButtonModule,
    SelectModule,
    ProgressSpinnerModule,
    FormFieldComponent,
    BottomSheetComponent,
    NMultiselectComponent,
    PhoneInputComponent,
    TranslateModule,
  ],
  templateUrl: './editar-estabelecimento-modal.component.html',
  styleUrls: ['./editar-estabelecimento-modal.component.scss'],
})
export class EditarEstabelecimentoModalComponent {
  //#region Injeção de Dependências
  private fb = inject(FormBuilder);
  private notificacao = inject(NotificacaoService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  private estabelecimentoService = inject(EstabelecimentoService);
  private profissionalDataService = inject(ProfissionalDataService);
  private translateService = inject(TranslateService);
  private i18nService = inject(I18nService);
  private nichoTermosService = inject(NichoTermosService);
  private langChangeTrigger = toSignal(this.translateService.onLangChange);
  $nicho = this.nichoTermosService.$nicho;
  deviceService = inject(DeviceService);
  private ngZone = inject(NgZone);
  //#endregion

  //#region ViewChilds
  // ViewChild para os inputs de arquivo
  @ViewChild('fileInputCapa') fileInputCapa!: ElementRef<HTMLInputElement>;
  @ViewChild('fileInputPerfil') fileInputPerfil!: ElementRef<HTMLInputElement>;

  // ViewChild para templates do bottom sheet
  @ViewChild('headerTemplate', { static: true })
  headerTemplate!: TemplateRef<unknown>;
  @ViewChild('footerTemplate', { static: true })
  footerTemplate!: TemplateRef<unknown>;

  //#endregion

  //#region Inputs e Outputs

  visible = input.required<boolean>();
  estabelecimento = input<IEstabelecimento | null>(null);
  blocked = input<boolean>(false);
  closed = output<void>();
  saved = output<IEstabelecimento>();
  //#endregion

  //#region Signals

  $dropdownAppendTo = computed(() =>
    this.deviceService.$isMobile() ? undefined : 'body'
  );
  $loading = signal(false);
  $uploadImagemEmAndamento = computed(
    () => this.uploadingCapa() || this.uploadingPerfil()
  );
  $salvarDesabilitado = computed(
    () => this.$loading() || this.blocked() || this.$uploadImagemEmAndamento()
  );

  // Signals para upload de imagens - Capa
  uploadingCapa = signal<boolean>(false);
  uploadProgressCapa = signal<number>(0);
  dragActiveCapa = signal<boolean>(false);
  errorCapa = signal<boolean>(false);
  successCapa = signal<boolean>(false);
  $erroImagemCapa = signal<string | null>(null);

  // Signals para upload de imagens - Perfil
  uploadingPerfil = signal<boolean>(false);
  uploadProgressPerfil = signal<number>(0);
  dragActivePerfil = signal<boolean>(false);
  errorPerfil = signal<boolean>(false);
  successPerfil = signal<boolean>(false);
  $erroImagemPerfil = signal<string | null>(null);

  // Signal interno para controlar visibilidade (usado no bottom sheet)
  $internalVisible = signal<boolean>(false);
  $enderecoBrasil = signal(true);
  private ignorarLimpezaPais = false;
  private paisAnterior = PAIS_BRASIL;
  //#endregion

  //#region propriedades
  private diasFuncionamentoMap = new Map<number, string>();

  // Form
  $form!: FormGroup;

  // Preview das imagens e arquivos
  previewCapa: string | null = null;
  previewPerfil: string | null = null;
  arquivoCapa: File | null = null;
  arquivoPerfil: File | null = null;

  // Imagens modificadas para salvar
  imagensModificadas: IImagem[] = [];

  // Dados para selects
  estados: string[] = EstadosConst.sigla;
  paises: IPaisOption[] = listarPaises();

  $btnAbreFeriado = computed(() => {
    this.langChangeTrigger();
    this.i18nService.idioma();
    return [
      {
        label: this.translateService.instant('GLOBAL.SIM') || 'Sim',
        value: true,
      },
      {
        label: this.translateService.instant('GLOBAL.NAO') || 'Não',
        value: false,
      },
    ];
  });
  get btnAbreFeriado() {
    return this.$btnAbreFeriado();
  }

  $nichos = computed(() => {
    this.langChangeTrigger();
    this.i18nService.idioma();
    return [
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.BELEZA_ESTETICA'
          ) || 'Beleza & Estética (Barbearia, Salão, Estética)',
        value: NichoEstabelecimentoEnum.BELEZA_ESTETICA,
      },
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.SAUDE_BEM_ESTAR'
          ) || 'Saúde & Bem-Estar (Clínicas, Médicos, Consultórios)',
        value: NichoEstabelecimentoEnum.SAUDE_BEM_ESTAR,
      },
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.FITNESS_ESPORTES'
          ) || 'Fitness & Studios (Pilates, Personal, Crossfit)',
        value: NichoEstabelecimentoEnum.FITNESS_ESPORTES,
      },
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.PET_CARE'
          ) || 'Pet & Veterinária (Petshops, Grooming, Vets)',
        value: NichoEstabelecimentoEnum.PET_CARE,
      },
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.CONSULTORIA_SERVICOS'
          ) || 'Consultoria & Serviços (Advocacia, Contabilidade)',
        value: NichoEstabelecimentoEnum.CONSULTORIA_SERVICOS,
      },
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.ESTUDIO_ARTE'
          ) || 'Arte & Estúdio (Tatuagem, Piercing, Fotografia)',
        value: NichoEstabelecimentoEnum.ESTUDIO_ARTE,
      },
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.SERVICOS_TECNICOS'
          ) || 'Serviços Técnicos & Manutenção (Pintor, Eletricista, Encanador)',
        value: NichoEstabelecimentoEnum.SERVICOS_TECNICOS,
      },
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.INFORMATICA'
          ) || 'Informática & Assistência Técnica (Computadores, Notebooks)',
        value: NichoEstabelecimentoEnum.INFORMATICA,
      },
      {
        label:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.NICHOS.AUTOMOTIVO'
          ) || 'Oficina Automotiva (Mecânica, Funilaria, Auto Center)',
        value: NichoEstabelecimentoEnum.AUTOMOTIVO,
      },
    ];
  });
  get nichos() {
    return this.$nichos();
  }

  $diasSemana = computed(() => {
    this.langChangeTrigger();
    this.i18nService.idioma();
    return [
      {
        id: 1,
        nome:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.DOMINGO'
          ) || 'Domingo',
      },
      {
        id: 2,
        nome:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.SEGUNDA'
          ) || 'Segunda-feira',
      },
      {
        id: 3,
        nome:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.TERCA'
          ) || 'Terça-feira',
      },
      {
        id: 4,
        nome:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.QUARTA'
          ) || 'Quarta-feira',
      },
      {
        id: 5,
        nome:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.QUINTA'
          ) || 'Quinta-feira',
      },
      {
        id: 6,
        nome:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.SEXTA'
          ) || 'Sexta-feira',
      },
      {
        id: 7,
        nome:
          this.translateService.instant(
            'BARDO_ESTABELECIMENTOS.DIAS_SEMANA.SABADO'
          ) || 'Sábado',
      },
    ];
  });
  get diasSemana() {
    return this.$diasSemana();
  }

  private readonly imagemConfig = {
    capa: { maxWidth: 1600, maxHeight: 640, maxSizeKB: 900 },
    perfil: { maxWidth: 600, maxHeight: 600, maxSizeKB: 250 },
  } as const;
  private readonly maxImagemUploadBytes = 10 * 1024 * 1024;
  private readonly logUploadImagem = true;
  //#endregion

  constructor() {
    this.initForm();
    this.$form
      .get('endereco.pais')
      ?.valueChanges.pipe(takeUntilDestroyed())
      .subscribe((pais) => this.aplicarRegrasPais(pais));

    // Sincronizar visible input com signal interno
    effect(() => {
      this.$internalVisible.set(this.visible());
    });

    let wasVisible = false;
    // Effect para popular form apenas na abertura do modal
    effect(() => {
      const isVisible = this.visible();
      const estab = this.estabelecimento();

      if (isVisible && !wasVisible && estab) {
        this.populateForm(estab);
      }
      wasVisible = isVisible;
    });
  }

  get horariosInvalidos(): boolean {
    return !!(
      this.$form?.errors?.['fechamentoMenorQueAbertura'] &&
      (this.$form.get('horaAbertura')?.dirty ||
        this.$form.get('horaFechamento')?.dirty)
    );
  }

  private initForm(): void {
    this.$form = this.fb.group(
      {
        id: [0],
        nome: ['', [Validators.required, Validators.minLength(3)]],
        telefone: ['', Validators.required],
        horaAbertura: ['', Validators.required],
        horaFechamento: ['', Validators.required],
        diasFuncionamento: [[], Validators.required],
        abreFeriado: [false],
        nicho: [NichoEstabelecimentoEnum.BELEZA_ESTETICA, Validators.required],
        habilitarSelecaoPaciente: [false],
        redeSocial: [''],
        website: [''],
        status: [true],
        endereco: this.fb.group({
          id: [0],
          logradouro: ['', Validators.required],
          numero: ['', Validators.required],
          bairro: ['', Validators.required],
          cidade: ['', Validators.required],
          estado: ['', Validators.required],
          pais: [PAIS_BRASIL, Validators.required],
          complemento: [''],
          codPostal: ['', Validators.required],
        }),
      },
      { validators: fechamentoMaiorQueAbertura }
    );
  }

  aoSelecionarPais(pais: unknown): void {
    this.aplicarRegrasPais(pais);
  }

  ehEnderecoBrasil(): boolean {
    return this.extrairCodigoPais(this.$form?.get('endereco.pais')?.value) === PAIS_BRASIL;
  }

  private extrairCodigoPais(pais: unknown): string {
    if (pais && typeof pais === 'object') {
      if ('codigo' in pais) {
        return normalizarCodigoPais(String((pais as { codigo: unknown }).codigo ?? ''));
      }
      if ('value' in pais) {
        return this.extrairCodigoPais((pais as { value: unknown }).value);
      }
    }

    return normalizarCodigoPais(typeof pais === 'string' ? pais : null);
  }

  private aplicarRegrasPais(pais: unknown): void {
    const codigo = this.extrairCodigoPais(pais);
    const controlePais = this.$form.get('endereco.pais');
    if (controlePais && controlePais.value !== codigo) {
      controlePais.setValue(codigo, { emitEvent: false });
    }
    const brasil = codigo === PAIS_BRASIL;
    const mudouTipo = ehPaisBrasil(this.paisAnterior) !== brasil;
    this.paisAnterior = codigo;
    this.$enderecoBrasil.set(brasil);

    const endereco = this.$form.get('endereco') as FormGroup;
    const bairro = endereco.get('bairro');
    const complemento = endereco.get('complemento');
    const estado = endereco.get('estado');
    const codPostal = endereco.get('codPostal');

    if (brasil) {
      bairro?.setValidators([Validators.required, Validators.maxLength(100)]);
      estado?.setValidators([Validators.required]);
      codPostal?.setValidators([Validators.required, Validators.maxLength(20)]);
    } else {
      bairro?.clearValidators();
      estado?.setValidators([Validators.required, Validators.maxLength(100)]);
      codPostal?.setValidators([Validators.required, Validators.maxLength(20)]);
    }

    if (!this.ignorarLimpezaPais && mudouTipo) {
      if (brasil) {
        if (!this.estados.includes(String(estado?.value ?? ''))) {
          estado?.setValue('', { emitEvent: false });
        }
        const digitos = String(codPostal?.value ?? '').replace(/\D/g, '');
        if (digitos.length !== 8) {
          codPostal?.setValue('', { emitEvent: false });
        }
        if (bairro?.value == null) {
          bairro?.setValue('', { emitEvent: false });
        }
      } else {
        bairro?.setValue(null, { emitEvent: false });
        complemento?.setValue(null, { emitEvent: false });
        if (this.estados.includes(String(estado?.value ?? ''))) {
          estado?.setValue('', { emitEvent: false });
        }
        const digitos = String(codPostal?.value ?? '').replace(/\D/g, '');
        if (digitos.length === 8) {
          codPostal?.setValue('', { emitEvent: false });
        }
      }
    }

    bairro?.updateValueAndValidity({ emitEvent: false });
    complemento?.updateValueAndValidity({ emitEvent: false });
    estado?.updateValueAndValidity({ emitEvent: false });
    codPostal?.updateValueAndValidity({ emitEvent: false });
  }

  private enderecoAlterado(
    atual: IEndereco | null | undefined,
    informado: IEndereco,
    pais: string
  ): boolean {
    if (!atual) return true;
    return (
      normalizarCodigoPais(atual.pais) !== pais ||
      (atual.logradouro ?? '') !== (informado.logradouro ?? '') ||
      (atual.numero ?? '') !== (informado.numero ?? '') ||
      (atual.bairro ?? '') !== (informado.bairro ?? '') ||
      (atual.cidade ?? '') !== (informado.cidade ?? '') ||
      (atual.estado ?? '') !== (informado.estado ?? '') ||
      (atual.complemento ?? '') !== (informado.complemento ?? '') ||
      (atual.codPostal ?? '') !== (informado.codPostal ?? '')
    );
  }

  private populateForm(estabelecimento: IEstabelecimento): void {
    // Converter horários string (HH:mm) para Date objects para o p-calendar (desktop)
    // ou manter como string "HH:mm" para input nativo (mobile)
    let horaAbertura: Date | string | null = null;
    let horaFechamento: Date | string | null = null;

    if (this.deviceService.$isMobile()) {
      horaAbertura = estabelecimento.horaAbertura || null;
      horaFechamento = estabelecimento.horaFechamento || null;
    } else {
      if (estabelecimento.horaAbertura) {
        const [hours, minutes] = estabelecimento.horaAbertura.split(':');
        horaAbertura = new Date();
        horaAbertura.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      }

      if (estabelecimento.horaFechamento) {
        const [hours, minutes] = estabelecimento.horaFechamento.split(':');
        horaFechamento = new Date();
        horaFechamento.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      }
    }

    const pais = normalizarCodigoPais(estabelecimento.endereco?.pais);
    this.ignorarLimpezaPais = true;
    this.$form.patchValue({
      id: estabelecimento.id || 0,
      nome: estabelecimento.nome,
      telefone: estabelecimento.telefone,
      horaAbertura: horaAbertura,
      horaFechamento: horaFechamento,
      diasFuncionamento: estabelecimento.diasFuncionamento?.map((m) => m.diaId),
      abreFeriado: estabelecimento.abreFeriado ?? false,
      nicho: estabelecimento.nicho || NichoEstabelecimentoEnum.BELEZA_ESTETICA,
      habilitarSelecaoPaciente:
        estabelecimento.habilitarSelecaoPaciente ?? false,
      redeSocial: estabelecimento.redeSocial || '',
      website: estabelecimento.website || '',
      status: estabelecimento.status,
      endereco: {
        id: estabelecimento.endereco?.id || 0,
        logradouro: estabelecimento.endereco?.logradouro || '',
        numero: estabelecimento.endereco?.numero || '',
        bairro: ehPaisBrasil(pais) ? estabelecimento.endereco?.bairro || '' : null,
        cidade: estabelecimento.endereco?.cidade || '',
        estado: estabelecimento.endereco?.estado || '',
        pais,
        complemento: ehPaisBrasil(pais)
          ? estabelecimento.endereco?.complemento || ''
          : null,
        codPostal: estabelecimento.endereco?.codPostal || '',
      },
    });
    this.aplicarRegrasPais(pais);
    this.ignorarLimpezaPais = false;

    // Carregar imagens existentes
    this.carregarImagensExistentes();
  }

  private carregarImagensExistentes(): void {
    this.resetarImagemStates();

    const estab = this.estabelecimento();
    if (estab?.imagens) {
      // Carregar foto de capa
      const fotoCapa = estab.imagens.find((img) => img.token === 'FOTO_CAPA');
      if (fotoCapa) {
        this.previewCapa = `data:${fotoCapa.tipo};base64,${fotoCapa.conteudo}`;
      }

      // Carregar foto de perfil
      const fotoPerfil = estab.imagens.find(
        (img) => img.token === 'FOTO_PERFIL'
      );
      if (fotoPerfil) {
        this.previewPerfil = `data:${fotoPerfil.tipo};base64,${fotoPerfil.conteudo}`;
      }
    }
  }

  fecharModal(): void {
    this.$form.reset();
    this.resetarImagemStates();
    this.diasFuncionamentoMap.clear();
    this.$internalVisible.set(false);
    this.closed.emit();
  }

  // ========================================
  // MÉTODOS DE UPLOAD DE IMAGENS
  // ========================================

  private debugUpload(
    tipo: 'capa' | 'perfil',
    etapa: string,
    dados?: Record<string, unknown>,
    nivel: 'log' | 'warn' | 'error' = 'log'
  ): void {
    if (!this.logUploadImagem) return;

    console[nivel](`[UploadImagem:${tipo}] ${etapa}`, {
      mobile: this.deviceService.$isMobile(),
      ...dados,
    });
  }

  private resumoArquivo(file: File) {
    return {
      nome: file.name,
      tipo: file.type,
      tamanhoBytes: file.size,
      tamanhoMb: (file.size / (1024 * 1024)).toFixed(2),
      extensao: file.name.split('.').pop()?.toLowerCase() ?? '',
    };
  }

  selectImage(tipo: 'capa' | 'perfil'): void {
    if (tipo === 'capa') {
      this.fileInputCapa?.nativeElement?.click();
    } else {
      this.fileInputPerfil?.nativeElement?.click();
    }
  }

  onFileSelect(tipo: 'capa' | 'perfil', event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    this.debugUpload(tipo, 'onFileSelect:inicio', {
      temArquivo: !!file,
      arquivo: file ? this.resumoArquivo(file) : null,
    });

    if (!file) {
      this.debugUpload(tipo, 'onFileSelect:abortado', {
        motivo: 'input.files vazio',
      });
      return;
    }

    // Mobile: leitura deve iniciar de forma síncrona neste handler (sem await/Promise).
    this.lerArquivoDoInput(tipo, file, input);
  }

  /** Valida e inicia a leitura no mesmo turno do evento change (obrigatório no mobile). */
  private lerArquivoDoInput(
    tipo: 'capa' | 'perfil',
    file: File,
    input: HTMLInputElement
  ): void {
    this.resetarEstados(tipo);
    this.limparErroImagem(tipo);

    if (!this.isArquivoImagem(file)) {
      input.value = '';
      this.debugUpload(
        tipo,
        'onFileSelect:bloqueado',
        { motivo: 'formato-invalido' },
        'warn'
      );
      this.mostrarErro(
        tipo,
        this.translateService.instant(
          'BARDO_ESTABELECIMENTOS.MODAL.ERRO_FORMATO_IMAGEM'
        ) || 'Selecione uma imagem nos formatos JPG, PNG ou WEBP.'
      );
      return;
    }

    if (file.size > this.maxImagemUploadBytes) {
      input.value = '';
      this.debugUpload(
        tipo,
        'onFileSelect:bloqueado',
        {
          motivo: 'tamanho-excedido',
          limiteMb: 10,
          tamanhoMb: (file.size / (1024 * 1024)).toFixed(2),
        },
        'warn'
      );
      this.mostrarErro(
        tipo,
        this.translateService.instant(
          'BARDO_ESTABELECIMENTOS.MODAL.ERRO_TAMANHO_IMAGEM'
        ) || 'A imagem deve ter no máximo 10MB.'
      );
      return;
    }

    if (file.size === 0) {
      input.value = '';
      this.debugUpload(
        tipo,
        'onFileSelect:bloqueado',
        { motivo: 'arquivo-vazio' },
        'warn'
      );
      this.mostrarErro(
        tipo,
        this.translateService.instant(
          'BARDO_ESTABELECIMENTOS.MODAL.ERRO_ARQUIVO_VAZIO'
        ) || 'O arquivo selecionado está vazio. Tente outra imagem.'
      );
      return;
    }

    this.iniciarLeituraImagem(tipo, file, input);
  }
  //#region  eventos
  onDragOver(event: DragEvent, tipo: 'capa' | 'perfil'): void {
    event.preventDefault();
    event.stopPropagation();

    if (tipo === 'capa') {
      this.dragActiveCapa.set(true);
    } else {
      this.dragActivePerfil.set(true);
    }
  }

  onDragLeave(event: DragEvent, tipo: 'capa' | 'perfil'): void {
    event.preventDefault();
    event.stopPropagation();

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      if (tipo === 'capa') {
        this.dragActiveCapa.set(false);
      } else {
        this.dragActivePerfil.set(false);
      }
    }
  }

  onDrop(event: DragEvent, tipo: 'capa' | 'perfil'): void {
    event.preventDefault();
    event.stopPropagation();

    if (tipo === 'capa') {
      this.dragActiveCapa.set(false);
    } else {
      this.dragActivePerfil.set(false);
    }

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processarArquivo(tipo, files[0]);
    }
  }
  //#endregion
  removeImage(tipo: 'capa' | 'perfil'): void {
    if (tipo === 'capa') {
      this.previewCapa = null;
      this.arquivoCapa = null;
      if (this.fileInputCapa?.nativeElement) {
        this.fileInputCapa.nativeElement.value = '';
      }
      this.resetarEstadosCapa();
      this.marcarImagemParaRemocao('FOTO_CAPA');
    } else {
      this.previewPerfil = null;
      this.arquivoPerfil = null;
      if (this.fileInputPerfil?.nativeElement) {
        this.fileInputPerfil.nativeElement.value = '';
      }
      this.resetarEstadosPerfil();
      this.marcarImagemParaRemocao('FOTO_PERFIL');
    }
  }

  private marcarImagemParaRemocao(token: 'FOTO_CAPA' | 'FOTO_PERFIL'): void {
    // Remove da lista de imagens modificadas se já estava lá
    this.imagensModificadas = this.imagensModificadas.filter(
      (img) => img.token !== token
    );

    // Se a imagem existia no estabelecimento original, marcar como removida
    const estab = this.estabelecimento();
    const imagemExistente = estab?.imagens?.find((img) => img.token === token);
    if (imagemExistente) {
      this.imagensModificadas.push({
        ...imagemExistente,
        conteudo: '', // Conteúdo vazio indica remoção
      });
    }
  }

  private processarArquivo(tipo: 'capa' | 'perfil', file: File): void {
    this.debugUpload(tipo, 'processarArquivo:inicio', {
      arquivo: this.resumoArquivo(file),
      tipoInferido: this.inferirTipoImagem(file),
      isImagem: this.isArquivoImagem(file),
    });

    this.resetarEstados(tipo);
    this.limparErroImagem(tipo);

    if (!this.isArquivoImagem(file)) {
      this.debugUpload(
        tipo,
        'processarArquivo:bloqueado',
        { motivo: 'formato-invalido' },
        'warn'
      );
      this.mostrarErro(
        tipo,
        this.translateService.instant(
          'BARDO_ESTABELECIMENTOS.MODAL.ERRO_FORMATO_IMAGEM'
        ) || 'Selecione uma imagem nos formatos JPG, PNG ou WEBP.'
      );
      return;
    }

    if (file.size > this.maxImagemUploadBytes) {
      this.debugUpload(
        tipo,
        'processarArquivo:bloqueado',
        {
          motivo: 'tamanho-excedido',
          limiteMb: 10,
          tamanhoMb: (file.size / (1024 * 1024)).toFixed(2),
        },
        'warn'
      );
      this.mostrarErro(
        tipo,
        this.translateService.instant(
          'BARDO_ESTABELECIMENTOS.MODAL.ERRO_TAMANHO_IMAGEM'
        ) || 'A imagem deve ter no máximo 10MB.'
      );
      return;
    }

    if (file.size === 0) {
      this.debugUpload(
        tipo,
        'processarArquivo:bloqueado',
        { motivo: 'arquivo-vazio' },
        'warn'
      );
      this.mostrarErro(
        tipo,
        this.translateService.instant(
          'BARDO_ESTABELECIMENTOS.MODAL.ERRO_ARQUIVO_VAZIO'
        ) || 'O arquivo selecionado está vazio. Tente outra imagem.'
      );
      return;
    }

    this.iniciarLeituraImagem(tipo, file);
  }

  private iniciarLeituraImagem(
    tipo: 'capa' | 'perfil',
    file: File,
    input?: HTMLInputElement
  ): void {
    if (tipo === 'capa') {
      this.uploadingCapa.set(true);
      this.uploadProgressCapa.set(40);
    } else {
      this.uploadingPerfil.set(true);
      this.uploadProgressPerfil.set(40);
    }

    this.processarViaObjectURL(tipo, file, input);
  }

  /**
   * Lê o arquivo via objectURL + Image (em vez de FileReader.readAsDataURL).
   * No mobile o FileReader dispara NotReadableError de forma intermitente; o
   * carregamento via <img> é mais confiável e já entrega o bitmap p/ o canvas.
   */
  private processarViaObjectURL(
    tipo: 'capa' | 'perfil',
    file: File,
    input?: HTMLInputElement
  ): void {
    this.debugUpload(tipo, 'objectURL:inicio', {
      arquivo: this.resumoArquivo(file),
    });

    const url = URL.createObjectURL(file);
    const img = new Image();

    const liberar = () => {
      URL.revokeObjectURL(url);
      if (input) input.value = '';
    };

    img.onload = () => {
      liberar();

      this.debugUpload(tipo, 'objectURL:imagem-carregada', {
        largura: img.width,
        altura: img.height,
      });

      try {
        const mimeType = this.inferirTipoImagem(file);
        const base64Comprimido = this.gerarBase64DeImagem(img, mimeType, tipo);

        if (!base64Comprimido?.startsWith('data:')) {
          throw new Error('compress-failed');
        }

        this.aplicarImagemProcessada(tipo, file, base64Comprimido, mimeType);
      } catch (erro) {
        this.debugUpload(
          tipo,
          'objectURL:erro-processar',
          {
            erro: erro instanceof Error ? erro.message : String(erro),
            arquivo: this.resumoArquivo(file),
          },
          'error'
        );

        this.ngZone.run(() =>
          this.tratarFalhaUpload(tipo, this.mensagemErroProcessamento(file))
        );
      }
    };

    img.onerror = () => {
      liberar();

      this.debugUpload(
        tipo,
        'objectURL:erro',
        { arquivo: this.resumoArquivo(file) },
        'error'
      );

      this.ngZone.run(() =>
        this.tratarFalhaUpload(tipo, this.mensagemErroProcessamento(file))
      );
    };

    img.src = url;
  }

  private aplicarImagemProcessada(
    tipo: 'capa' | 'perfil',
    file: File,
    base64Comprimido: string,
    mimeType: string
  ): void {
    this.debugUpload(tipo, 'compressao:sucesso', {
      tamanhoFinal: base64Comprimido.length,
    });

    this.ngZone.run(() => {
      this.limparErroImagem(tipo);

      if (tipo === 'capa') {
        this.uploadProgressCapa.set(100);
        this.previewCapa = base64Comprimido;
        this.arquivoCapa = file;
        this.uploadingCapa.set(false);
        this.successCapa.set(true);
        setTimeout(() => this.successCapa.set(false), 2000);
      } else {
        this.uploadProgressPerfil.set(100);
        this.previewPerfil = base64Comprimido;
        this.arquivoPerfil = file;
        this.uploadingPerfil.set(false);
        this.successPerfil.set(true);
        setTimeout(() => this.successPerfil.set(false), 2000);
      }

      this.prepararImagemParaSalvar(tipo, base64Comprimido, file.name, mimeType);

      this.debugUpload(tipo, 'upload:concluido', {
        token: tipo === 'capa' ? 'FOTO_CAPA' : 'FOTO_PERFIL',
        conteudoBase64Length: base64Comprimido.split(',')[1]?.length ?? 0,
      });
    });
  }

  private isArquivoImagem(file: File): boolean {
    if (file.type.startsWith('image/')) return true;

    const extensao = file.name.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'bmp'].includes(
      extensao ?? ''
    );
  }

  private inferirTipoImagem(file: File): string {
    if (file.type.startsWith('image/')) return file.type;

    const extensao = file.name.split('.').pop()?.toLowerCase();
    const tipos: Record<string, string> = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      heic: 'image/heic',
      heif: 'image/heif',
      bmp: 'image/bmp',
    };

    return tipos[extensao ?? ''] ?? 'image/jpeg';
  }

  private mensagemErroProcessamento(file: File): string {
    const extensao = file.name.split('.').pop()?.toLowerCase();

    if (['heic', 'heif'].includes(extensao ?? '')) {
      return (
        this.translateService.instant(
          'BARDO_ESTABELECIMENTOS.MODAL.ERRO_HEIC'
        ) ||
        'Formato HEIC/HEIF não é suportado neste dispositivo. Salve a foto como JPG ou PNG e tente novamente.'
      );
    }

    return (
      this.translateService.instant(
        'BARDO_ESTABELECIMENTOS.MODAL.ERRO_PROCESSAR_IMAGEM'
      ) ||
      'Não foi possível processar esta imagem. Use JPG, PNG ou WEBP com até 10MB.'
    );
  }

  private tratarFalhaUpload(tipo: 'capa' | 'perfil', mensagem: string): void {
    this.debugUpload(tipo, 'upload:falhou', { mensagem }, 'error');
    if (tipo === 'capa') {
      this.uploadingCapa.set(false);
      this.uploadProgressCapa.set(0);
    } else {
      this.uploadingPerfil.set(false);
      this.uploadProgressPerfil.set(0);
    }

    this.mostrarErro(tipo, mensagem);
  }

  private prepararImagemParaSalvar(
    tipo: 'capa' | 'perfil',
    base64: string,
    nomeArquivo: string,
    type: string
  ): void {
    const token = tipo === 'capa' ? 'FOTO_CAPA' : 'FOTO_PERFIL';
    const base64Limpo = base64.split(',')[1];

    // Remove da lista se já existia
    this.imagensModificadas = this.imagensModificadas.filter(
      (img) => img.token !== token
    );

    // Adiciona a nova imagem
    const estab = this.estabelecimento();
    const imagemExistente = estab?.imagens?.find((img) => img.token === token);

    this.imagensModificadas.push({
      id: imagemExistente?.id ?? '',
      estabelecimentoId:
        this.estabelecimentoDataService.$estabelecimentoAtual()?.id,
      nome: nomeArquivo,
      conteudo: base64Limpo,
      tipo: type,
      token: token,
    });
  }

  private mostrarErro(tipo: 'capa' | 'perfil', mensagem: string): void {
    this.ngZone.run(() => {
      if (tipo === 'capa') {
        this.$erroImagemCapa.set(mensagem);
        this.errorCapa.set(true);
      } else {
        this.$erroImagemPerfil.set(mensagem);
        this.errorPerfil.set(true);
      }

      this.notificacao.notificacaoWarn(mensagem);
    });
  }

  private limparErroImagem(tipo: 'capa' | 'perfil'): void {
    if (tipo === 'capa') {
      this.$erroImagemCapa.set(null);
      this.errorCapa.set(false);
    } else {
      this.$erroImagemPerfil.set(null);
      this.errorPerfil.set(false);
    }
  }
  //#region resetar estados
  private resetarEstados(tipo: 'capa' | 'perfil'): void {
    if (tipo === 'capa') {
      this.resetarEstadosCapa();
    } else {
      this.resetarEstadosPerfil();
    }
  }

  private resetarEstadosCapa(): void {
    this.uploadingCapa.set(false);
    this.uploadProgressCapa.set(0);
    this.dragActiveCapa.set(false);
    this.errorCapa.set(false);
    this.successCapa.set(false);
    this.$erroImagemCapa.set(null);
  }

  private resetarEstadosPerfil(): void {
    this.uploadingPerfil.set(false);
    this.uploadProgressPerfil.set(0);
    this.dragActivePerfil.set(false);
    this.errorPerfil.set(false);
    this.successPerfil.set(false);
    this.$erroImagemPerfil.set(null);
  }

  private resetarImagemStates(): void {
    this.previewCapa = null;
    this.previewPerfil = null;
    this.arquivoCapa = null;
    this.arquivoPerfil = null;
    this.imagensModificadas = [];
    this.resetarEstadosCapa();
    this.resetarEstadosPerfil();
  }
  //#endregion

  // ========================================
  // GETTERS PARA TEMPLATE
  // ========================================

  get imagemCapa(): string {
    return this.previewCapa || '';
  }

  get imagemPerfil(): string {
    return this.previewPerfil || '';
  }

  private horarioToMinutos(horario?: string): number {
    if (!horario) return 0;
    const partes = horario.split(':');
    return parseInt(partes[0] ?? '0') * 60 + parseInt(partes[1] ?? '0');
  }

  onTelefoneChange(value: string): void {
    const ctrl = this.$form.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  async salvar(): Promise<void> {
    if (this.$uploadImagemEmAndamento()) {
      this.notificacao.notificacaoWarn(
        'Aguarde o processamento da imagem antes de salvar.'
      );
      return;
    }

    if (this.$form.invalid) {
      this.$form.markAllAsTouched();
      if (this.$form.errors?.['fechamentoMenorQueAbertura']) {
        this.notificacao.notificacaoWarn(
          this.translateService.instant('PRIMEIRO_ACESSO.NOTIFICACAO_ERRO_FECHAMENTO')
        );
      } else {
        this.notificacao.notificacaoWarn(
          this.translateService.instant('BARDO_ESTABELECIMENTOS.MODAL.PREENCHA_OBRIGATORIOS')
        );
      }
      return;
    }

    this.$loading.set(true);

    try {
      const formValue = this.$form.getRawValue();

      // Converter Date objects ou string "HH:mm" para string HH:mm
      const horaAbertura = this.horaToString(formValue.horaAbertura);
      const horaFechamento = this.horaToString(formValue.horaFechamento);

      // Validar conflito de horários com profissionais antes de chamar a API
      const estabelecimentoAtual =
        this.estabelecimentoDataService.$estabelecimentoAtual();
      const aberturaAtual = this.horarioToMinutos(
        estabelecimentoAtual?.horaAbertura
      );
      const fechamentoAtual = this.horarioToMinutos(
        estabelecimentoAtual?.horaFechamento
      );
      const novaAbertura = this.horarioToMinutos(horaAbertura);
      const novoFechamento = this.horarioToMinutos(horaFechamento);

      if (
        novaAbertura !== aberturaAtual ||
        novoFechamento !== fechamentoAtual
      ) {
        const profissionaisConflitantes = this.profissionalDataService
          .$profissionais()
          .filter((prof) => {
            const inicioProfissional = this.horarioToMinutos(
              prof.inicioExpediente
            );
            const fimProfissional = this.horarioToMinutos(prof.fimExpediente);
            return (
              inicioProfissional < novaAbertura ||
              fimProfissional > novoFechamento
            );
          });

        if (profissionaisConflitantes.length > 0) {
          const nomes = profissionaisConflitantes.map((p) => p.nome).join(', ');
          this.notificacao.notificacaoWarn(
            this.translateService.instant(
              'BARDO_ESTABELECIMENTOS.MODAL.CONFLITO_HORARIO_PROFISSIONAIS',
              { nomes }
            ) ||
            `Não é possível alterar os horários, pois os seguintes profissionais têm expediente fora do novo horário: ${nomes}.`
          );
          this.$loading.set(false);
          return;
        }
      }

      // Preparar imagens para salvar
      const imagensFinais = this.prepararImagensParaSalvar();

      const enderecoForm = formValue.endereco as IEndereco;
      const pais = normalizarCodigoPais(enderecoForm.pais);
      const brasil = pais === PAIS_BRASIL;
      const enderecoAtual = this.estabelecimento()?.endereco;
      const enderecoMudou = this.enderecoAlterado(enderecoAtual, {
        ...enderecoForm,
        pais,
        bairro: brasil ? enderecoForm.bairro : null,
        complemento: brasil ? enderecoForm.complemento || null : null,
      }, pais);

      const dados: IEstabelecimento = {
        ...this.estabelecimentoDataService.$estabelecimentoAtual(),
        ...formValue,
        horaAbertura,
        horaFechamento,
        diasFuncionamento: formValue.diasFuncionamento.map(
          (m: number | string) => ({
            diaId: Number(m),
          })
        ),
        endereco: {
          ...enderecoAtual,
          ...enderecoForm,
          id: enderecoAtual?.id || enderecoForm.id,
          pais,
          bairro: brasil ? enderecoForm.bairro : null,
          complemento: brasil ? enderecoForm.complemento || null : null,
          latitude: enderecoMudou ? null : enderecoAtual?.latitude,
          longitude: enderecoMudou ? null : enderecoAtual?.longitude,
        },
        imagens: imagensFinais,
      };

      const estabelecimentoId =
        this.estabelecimentoDataService.$estabelecimentoAtual()?.id;
      if (!estabelecimentoId) {
        throw new Error('ID do estabelecimento não encontrado');
      }

      await this.estabelecimentoService.atualizar(estabelecimentoId, dados);

      this.estabelecimentoDataService.updateEstabelecimento(estabelecimentoId, dados);

      this.fecharModal();
    } catch (error) {
      const mensagem =
        (error as { error?: { message?: string } })?.error?.message ||
        this.translateService.instant(
          'BARDO_ESTABELECIMENTOS.MODAL.ERRO_SALVAR'
        ) ||
        'Erro ao salvar estabelecimento';
      this.notificacao.notificacaoError(mensagem);
    } finally {
      this.$loading.set(false);
    }
  }

  private horaToString(val: Date | string | null): string {
    if (!val) return '';
    if (typeof val === 'string') return val.substring(0, 5);
    return `${val.getHours().toString().padStart(2, '0')}:${val
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;
  }

  private prepararImagensParaSalvar(): IImagem[] {
    const estab = this.estabelecimento();
    const imagensFinais: IImagem[] = [];

    // Adicionar imagens existentes que não foram modificadas
    if (estab?.imagens) {
      for (const imagemExistente of estab.imagens) {
        const foiModificada = this.imagensModificadas.some(
          (img) => img.token === imagemExistente.token
        );
        if (!foiModificada) {
          imagensFinais.push({
            ...imagemExistente,
            estabelecimentoId:
              this.estabelecimentoDataService.$estabelecimentoAtual()?.id,
          });
        }
      }
    }

    // Adicionar imagens modificadas (novas ou alteradas)
    for (const imagemModificada of this.imagensModificadas) {
      // Se conteúdo está vazio, significa que foi removida - não adicionar
      if (imagemModificada.conteudo.trim() !== '') {
        imagensFinais.push(imagemModificada);
      }
    }

    return imagensFinais;
  }
  //#region Formatação de imagem
  /** Redimensiona/recodifica a imagem já carregada (<img>) e devolve base64. */
  private gerarBase64DeImagem(
    img: HTMLImageElement,
    mimeType: string,
    tipo: 'capa' | 'perfil'
  ): string {
    const config = this.imagemConfig[tipo];
    const limiteBytes = config.maxSizeKB * 1024;

    const { width, height } = this.calcularDimensoesAlvo(
      img.width,
      img.height,
      config.maxWidth,
      config.maxHeight
    );

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('canvas-unavailable');
    }

    ctx.drawImage(img, 0, 0, width, height);

    const destino = mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
    let qualidade = destino === 'image/png' ? undefined : 0.85;
    let dataUrl = canvas.toDataURL(destino, qualidade);

    while (
      destino !== 'image/png' &&
      this.calcularTamanhoBase64(dataUrl) > limiteBytes &&
      qualidade &&
      qualidade > 0.4
    ) {
      qualidade = qualidade - 0.1;
      dataUrl = canvas.toDataURL(destino, qualidade);
    }

    return dataUrl;
  }

  private calcularDimensoesAlvo(
    larguraOriginal: number,
    alturaOriginal: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    let width = larguraOriginal;
    let height = alturaOriginal;
    const proporcao = width / height;

    if (width > maxWidth) {
      width = maxWidth;
      height = Math.round(width / proporcao);
    }

    if (height > maxHeight) {
      height = maxHeight;
      width = Math.round(height * proporcao);
    }

    return { width, height };
  }

  private calcularTamanhoBase64(base64: string): number {
    const pureBase64 = base64.includes(',') ? base64.split(',')[1] : base64;
    const padding = (pureBase64.match(/=+$/) || [''])[0].length;
    return pureBase64.length * 0.75 - padding;
  }
  //#endregion
}
